'use client';

import { recoverFromChunkLoadError } from '~/utils/chunk-load-recovery';
import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';

import '../styles/globals.css';

/** Last-resort boundary. `[locale]/layout.tsx` is the root layout (there is no `app/layout.tsx`),
 * and a Next.js `error.tsx` never catches throws from its own segment's layout — so every
 * root-layout failure skips the friendlier `[locale]/error.tsx` and lands here.
 *
 * That means this replaces the root layout: it renders its own document and cannot use the i18n,
 * session or notification providers, since a failure in one of them is exactly how a visitor gets
 * here. Hence the untranslated copy, matching `[locale]/error.tsx`. (FUN-5052) */
export default function GlobalError(props: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    useEffect(() => {
        Sentry.captureException(props.error);

        /* A chunk the root layout needs can fail here too, and this boundary has replaced the
         * layout — so nothing further up the tree is left to recover. After the capture, so the
         * event is on its way out before the page is replaced. */
        recoverFromChunkLoadError(props.error);
    }, [props.error]);

    return (
        <html lang="en">
            <body className="bg-blue">
                <main className="flex min-h-screen items-center justify-center p-6">
                    <div className="flex w-full max-w-xl flex-col gap-6 rounded-4xl bg-white p-8 sm:p-12">
                        <div className="flex flex-col gap-2">
                            <span className="text-4xl font-extrabold text-secondary">Oops!</span>
                            <h1 className="text-3xl font-extrabold text-primary sm:text-4xl">
                                Something went wrong
                            </h1>
                        </div>

                        <p className="text-primary-600">
                            We couldn&apos;t load this page. Please try again — if it keeps
                            happening, contact us at{' '}
                            <a
                                href="mailto:info@funkey.be"
                                className="font-medium text-secondary hover:underline"
                            >
                                info@funkey.be
                            </a>
                            .
                        </p>

                        <div className="flex flex-col gap-3 sm:flex-row">
                            <button
                                type="button"
                                onClick={props.reset}
                                className="rounded-full bg-secondary px-6 py-3 font-medium text-white hover:bg-secondary-600"
                            >
                                Try again
                            </button>
                            {/* oxlint-disable-next-line nextjs/no-html-link-for-pages -- the root layout is broken here, so a full document load is the only way back. */}
                            <a
                                href="/"
                                className="rounded-full border border-primary-200 px-6 py-3 text-center font-medium text-primary hover:bg-primary-50"
                            >
                                Back to home
                            </a>
                        </div>

                        {/* The digest is the only handle support has on the underlying exception:
                            it correlates this screen with the Sentry event. */}
                        {!!props.error.digest && (
                            <p className="text-sm text-primary-500">
                                Reference: <code>{props.error.digest}</code>
                            </p>
                        )}
                    </div>
                </main>
            </body>
        </html>
    );
}
